import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Loader2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Combobox } from '@/components/ui/combobox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { format } from 'date-fns';

interface AllocationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: () => void;
  editId?: string | null;
}

interface FormData {
  partnerId: string;
  allocationType: string;
  name: string;
  reference: string;
  validFrom: string;
  validTo: string;
  totalUnits: number;
  unitCost: number;
  currency: string;
  hotelRateId: string;
  notes: string;
}

const ALLOCATION_TYPES = [
  { value: 'HOTEL_ROOM', label: 'types.hotelRoom' },
  { value: 'TOUR_SEAT', label: 'types.tourSeat' },
  { value: 'TRANSFER_VEHICLE', label: 'types.transferVehicle' },
  { value: 'FLIGHT_SEAT', label: 'types.flightSeat' },
];

const CURRENCIES = ['USD', 'EUR', 'TRY', 'GBP'];

export function AllocationModal({
  open,
  onOpenChange,
  onSuccess,
  editId,
}: AllocationModalProps) {
  const { t } = useTranslation('allocations');
  const { t: tCommon } = useTranslation('common');

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      partnerId: '',
      allocationType: 'HOTEL_ROOM',
      name: '',
      reference: '',
      validFrom: format(new Date(), 'yyyy-MM-dd'),
      validTo: format(new Date(Date.now() + 90 * 24 * 60 * 60 * 1000), 'yyyy-MM-dd'),
      totalUnits: 10,
      unitCost: 0,
      currency: 'USD',
      hotelRateId: '',
      notes: '',
    },
  });

  const selectedPartnerId = watch('partnerId');
  const selectedType = watch('allocationType');

  // Queries
  const partnersQuery = trpc.partner.list.useQuery({ page: 1 });
  const allocationQuery = trpc.allocation.byId.useQuery(editId!, {
    enabled: !!editId,
  });

  // Get hotel rates for selected partner (only for hotel type)
  const hotelRatesQuery = trpc.partner.byId.useQuery(selectedPartnerId, {
    enabled: !!selectedPartnerId && selectedType === 'HOTEL_ROOM',
  });

  // Mutations
  const createMutation = trpc.allocation.create.useMutation({
    onSuccess: () => {
      showSuccessToast(t('messages.created'));
      onOpenChange(false);
      reset();
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  const updateMutation = trpc.allocation.update.useMutation({
    onSuccess: () => {
      showSuccessToast(t('messages.updated'));
      onOpenChange(false);
      reset();
      onSuccess?.();
    },
    onError: (error) => {
      showErrorToast(error.message);
    },
  });

  // Load edit data
  useEffect(() => {
    if (editId && allocationQuery.data) {
      const allocation = allocationQuery.data;
      reset({
        partnerId: allocation.partner.id,
        allocationType: allocation.allocationType,
        name: allocation.name,
        reference: allocation.reference ?? '',
        validFrom: format(new Date(allocation.validFrom), 'yyyy-MM-dd'),
        validTo: format(new Date(allocation.validTo), 'yyyy-MM-dd'),
        totalUnits: allocation.totalUnits,
        unitCost: allocation.unitCost,
        currency: allocation.currency,
        hotelRateId: allocation.hotelRate?.id ?? '',
        notes: allocation.notes ?? '',
      });
    }
  }, [editId, allocationQuery.data, reset]);

  // Reset when closing
  useEffect(() => {
    if (!open) {
      reset();
    }
  }, [open, reset]);

  const onSubmit = (data: FormData) => {
    const payload = {
      partnerId: data.partnerId,
      allocationType: data.allocationType as 'HOTEL_ROOM' | 'TOUR_SEAT' | 'TRANSFER_VEHICLE' | 'FLIGHT_SEAT',
      name: data.name,
      reference: data.reference || undefined,
      validFrom: new Date(data.validFrom),
      validTo: new Date(data.validTo),
      totalUnits: data.totalUnits,
      unitCost: data.unitCost,
      currency: data.currency,
      hotelRateId: data.hotelRateId || undefined,
      notes: data.notes || undefined,
    };

    if (editId) {
      updateMutation.mutate({ id: editId, ...payload });
    } else {
      createMutation.mutate(payload);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending;
  const partners = partnersQuery.data?.partners ?? [];
  const hotelRates = hotelRatesQuery.data?.hotelRates ?? [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {editId ? t('dialogs.edit.title') : t('dialogs.create.title')}
          </DialogTitle>
          <DialogDescription>
            {editId ? t('dialogs.edit.description') : t('dialogs.create.description')}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2">
            {/* Partner */}
            <div className="space-y-2">
              <Label>{t('fields.partner')} *</Label>
              <Combobox
                options={partners.map((partner) => ({
                  value: partner.id,
                  label: partner.companyName,
                }))}
                value={watch('partnerId')}
                onValueChange={(v) => setValue('partnerId', v)}
                placeholder={t('placeholders.selectPartner')}
                searchPlaceholder="Search partners..."
              />
              {errors.partnerId && (
                <p className="text-sm text-destructive">{tCommon('validation.required')}</p>
              )}
            </div>

            {/* Allocation Type */}
            <div className="space-y-2">
              <Label>{t('fields.type')} *</Label>
              <Combobox
                options={ALLOCATION_TYPES.map((type) => ({
                  value: type.value,
                  label: t(type.label),
                }))}
                value={watch('allocationType')}
                onValueChange={(v) => setValue('allocationType', v)}
                placeholder="Select type"
                searchPlaceholder="Search..."
              />
            </div>
          </div>

          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="name">{t('fields.name')} *</Label>
            <Input
              id="name"
              {...register('name', { required: true })}
              placeholder={t('placeholders.name')}
            />
            {errors.name && (
              <p className="text-sm text-destructive">{tCommon('validation.required')}</p>
            )}
          </div>

          {/* Reference */}
          <div className="space-y-2">
            <Label htmlFor="reference">{t('fields.reference')}</Label>
            <Input
              id="reference"
              {...register('reference')}
              placeholder={t('placeholders.reference')}
            />
          </div>

          {/* Hotel Rate (only for hotel type) */}
          {selectedType === 'HOTEL_ROOM' && selectedPartnerId && (
            <div className="space-y-2">
              <Label>{t('fields.hotelRate')}</Label>
              <Combobox
                options={[
                  { value: 'none', label: '-' },
                  ...hotelRates.map((rate) => ({
                    value: rate.id,
                    label: `${rate.roomType} - ${rate.seasonName}`,
                  })),
                ]}
                value={watch('hotelRateId') || 'none'}
                onValueChange={(v) => setValue('hotelRateId', v === 'none' ? '' : v)}
                placeholder={t('placeholders.selectHotelRate')}
                searchPlaceholder="Search rates..."
              />
            </div>
          )}

          <div className="grid gap-4 md:grid-cols-2">
            {/* Valid From */}
            <div className="space-y-2">
              <Label htmlFor="validFrom">{t('fields.validFrom')} *</Label>
              <Input
                id="validFrom"
                type="date"
                {...register('validFrom', { required: true })}
              />
              {errors.validFrom && (
                <p className="text-sm text-destructive">{tCommon('validation.required')}</p>
              )}
            </div>

            {/* Valid To */}
            <div className="space-y-2">
              <Label htmlFor="validTo">{t('fields.validTo')} *</Label>
              <Input
                id="validTo"
                type="date"
                {...register('validTo', { required: true })}
              />
              {errors.validTo && (
                <p className="text-sm text-destructive">{tCommon('validation.required')}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            {/* Total Units */}
            <div className="space-y-2">
              <Label htmlFor="totalUnits">{t('fields.totalUnits')} *</Label>
              <Input
                id="totalUnits"
                type="number"
                min="1"
                {...register('totalUnits', { valueAsNumber: true, required: true, min: 1 })}
              />
              {errors.totalUnits && (
                <p className="text-sm text-destructive">{tCommon('validation.required')}</p>
              )}
            </div>

            {/* Unit Cost */}
            <div className="space-y-2">
              <Label htmlFor="unitCost">{t('fields.unitCost')} *</Label>
              <Input
                id="unitCost"
                type="number"
                step="0.01"
                min="0"
                {...register('unitCost', { valueAsNumber: true, required: true, min: 0 })}
              />
              {errors.unitCost && (
                <p className="text-sm text-destructive">{tCommon('validation.required')}</p>
              )}
            </div>

            {/* Currency */}
            <div className="space-y-2">
              <Label>{tCommon('currency')}</Label>
              <Combobox
                options={CURRENCIES.map((cur) => ({
                  value: cur,
                  label: cur,
                }))}
                value={watch('currency')}
                onValueChange={(v) => setValue('currency', v)}
                placeholder="Select currency"
                searchPlaceholder="Search..."
              />
            </div>
          </div>

          {/* Notes */}
          <div className="space-y-2">
            <Label htmlFor="notes">{t('fields.notes')}</Label>
            <Textarea id="notes" {...register('notes')} rows={3} />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {tCommon('cancel')}
            </Button>
            <Button type="submit" disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {editId ? tCommon('save') : tCommon('create')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
